import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { getSession } from '../../utils/authUtils';
import { styled } from '@mui/material/styles';

const SectionContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '715px',
  backgroundColor: '#0f172a', // slate-900
  padding: '80px 24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '40px',
  [theme.breakpoints.up('md')]: {
    padding: '128px 48px'
  },
  [theme.breakpoints.up('lg')]: {
    padding: '128px 64px'
  },
  [theme.breakpoints.down('md')]: {
    minHeight: 'auto',
    gap: '30px'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 800,
  fontSize: '46px',
  textAlign: 'center',
  letterSpacing: '-0.025em',
  lineHeight: 1.2,
  '& .black': {
    color: '#ffffff'
  },
  '& .blue': {
    background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '34px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px'
  }
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '18px',
  color: '#94a3b8', // slate-400
  textAlign: 'center',
  letterSpacing: '0',
  lineHeight: 1.6,
  maxWidth: '621px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px'
  }
}));

const PricingCard = styled(Box)(({ theme, featured }) => ({
  display: 'flex',
  width: '100%',
  maxWidth: featured ? '310px' : '280px',
  minHeight: featured ? '441px' : '392px',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: featured ? '54px 18px' : '35px 17px',
  backgroundColor: '#1e293b', // slate-800
  borderRadius: '24px',
  border: featured ? '1px solid #22d3ee' : '1px solid #334155', // cyan or slate-700
  boxShadow: featured 
    ? '0 0 40px rgba(34, 211, 238, 0.3)'
    : '0 0 20px rgba(0, 0, 0, 0.3)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: featured 
      ? '0 0 50px rgba(34, 211, 238, 0.4)'
      : '0 0 30px rgba(34, 211, 238, 0.2)'
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    minHeight: 'auto'
  },
  [theme.breakpoints.down('sm')]: {
    padding: featured ? '40px 20px' : '30px 20px'
  }
}));

const PopularBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '150px',
  height: '40px',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '13px 17px',
  position: 'absolute',
  top: '-20px',
  background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
  borderRadius: '12px',
  border: '1px solid #334155',
  [theme.breakpoints.down('sm')]: {
    width: '120px',
    height: '36px',
    padding: '10px 12px'
  }
}));

const GetStartedButton = styled(Button)(({ theme, featured }) => ({
  width: '100%',
  maxWidth: '200px',
  height: '48px',
  borderRadius: '27px',
  backgroundColor: featured ? '#22d3ee' : 'transparent',
  color: featured ? '#0f172a' : '#22d3ee',
  border: featured ? 'none' : '1px solid #22d3ee',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  textTransform: 'none',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: featured ? '#a5b4fc' : 'rgba(34, 211, 238, 0.1)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(34, 211, 238, 0.3)'
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    fontSize: '14px'
  }
}));

const ToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  padding: '4px',
  backgroundColor: '#1e293b', // slate-800
  border: '1px solid #334155',
  borderRadius: '27px',
  marginBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxWidth: '300px'
  }
}));

const ToggleButton = styled(Button)(({ theme, active }) => ({
  flex: 1,
  minWidth: '120px',
  height: '40px',
  borderRadius: '24px',
  backgroundColor: active ? '#22d3ee' : 'transparent',
  color: active ? '#0f172a' : '#94a3b8',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: active ? '#a5b4fc' : 'rgba(34, 211, 238, 0.1)'
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: '100px',
    fontSize: '13px'
  }
}));

const PricingPlansSection = () => {
  const [billingCycle, setBillingCycle] = useState('yearly');
  const navigate = useNavigate();

  const plansData = {
    monthly: [
      {
        name: 'Basic',
        price: '₹1,199',
        description: 'Perfect for small/local businesses just getting started.',
        features: [
          'Keyword Research & On-Page SEO',
          'Google Business Profile Setup',
          'Local Directory Submissions',
          'Monthly SEO Report'
        ],
        featured: false
      },
      {
        name: 'Advanced',
        price: '₹1,999',
        description: 'Best for businesses ready to scale traffic & visibility.',
        features: [
          'Everything in Basic, plus:',
          'Competitor keyword analysis',
          'High-quality backlink building',
          'Bi-weekly growth reports'
        ],
        featured: true
      },
      {
        name: 'Pro',
        price: '₹2,999',
        description: 'For brands that want to dominate search rankings.',
        features: [
          'Everything in Advanced, plus:',
          'Multi-location SEO strategy',
          'Content marketing campaigns',
          'Dedicated SEO manager'
        ],
        featured: false
      }
    ],
    yearly: [
      {
        name: 'Basic',
        price: '₹11,999',
        description: 'Perfect for small/local businesses just getting started.',
        features: [
          'Keyword Research & On-Page SEO',
          'Google Business Profile Setup',
          'Local Directory Submissions',
          'Monthly SEO Report'
        ],
        featured: false
      },
      {
        name: 'Advanced',
        price: '₹19,999',
        description: 'Best for businesses ready to scale traffic & visibility.',
        features: [
          'Everything in Basic, plus:',
          'Competitor keyword analysis',
          'High-quality backlink building',
          'Bi-weekly growth reports'
        ],
        featured: true
      },
      {
        name: 'Pro',
        price: '₹29,999',
        description: 'For brands that want to dominate search rankings.',
        features: [
          'Everything in Advanced, plus:',
          'Multi-location SEO strategy',
          'Content marketing campaigns',
          'Dedicated SEO manager'
        ],
        featured: false
      }
    ]
  };

  const plans = plansData[billingCycle];

  return (
    <SectionContainer>
      <SectionTitle>
        <span className="black">Choose Our </span>
        <span className="blue">Plans</span>
      </SectionTitle>
      <SectionSubtitle>
        Simple, transparent, and result-driven pricing.
      </SectionSubtitle>

      {/* Billing Cycle Toggle */}
      <ToggleContainer>
        <ToggleButton 
          active={billingCycle === 'monthly'} 
          onClick={() => setBillingCycle('monthly')}
        >
          Monthly
        </ToggleButton>
        <ToggleButton 
          active={billingCycle === 'yearly'} 
          onClick={() => setBillingCycle('yearly')}
        >
          Yearly
        </ToggleButton>
      </ToggleContainer>

      <Box sx={{ 
        display: 'flex', 
        gap: { xs: '20px', md: '30px' }, 
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'stretch',
        justifyContent: 'center',
        width: '100%',
        maxWidth: '1200px',
        marginTop: '20px'
      }}>
        {plans.map((plan, index) => (
          <Box key={index} sx={{ position: 'relative', display: 'flex', flex: { xs: '0 0 auto', md: '1' }, maxWidth: { xs: '100%', md: '350px' } }}>
            {plan.featured && (
              <PopularBadge>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <img style={{ width: '20px', height: '20px' }} alt="Star" src="https://c.animaapp.com/mgbvruh0ta8bZ7/img/material-symbols-star.svg" />
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: { xs: '12px', sm: '14px' }, color: '#0f172a', textAlign: 'center', letterSpacing: '0', lineHeight: '28px', whiteSpace: 'nowrap' }}>
                    Most Popular
                  </Typography>
                </Box>
              </PopularBadge>
            )}
            <PricingCard featured={plan.featured}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'center', gap: plan.featured ? '32px' : '27px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', width: '100%' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '19px', width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: { xs: '16px', sm: '18px' }, color: '#ffffff', textAlign: 'center', lineHeight: '24px', marginTop: '-1px', width: '100%' }}>
                        {plan.name}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: { xs: '11px', sm: '12px' }, color: '#94a3b8', textAlign: 'center', letterSpacing: '0', lineHeight: '16px', minHeight: '40px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {plan.description}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: { xs: '18px', sm: '20px' }, color: '#ffffff', letterSpacing: '0', lineHeight: '16px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontWeight: 600 }}>{plan.price}</span>
                      <span style={{ fontSize: '14px', fontWeight: 400 }}> / {billingCycle === 'monthly' ? 'month' : 'year'}</span>
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: { xs: '11px', sm: '12px' }, color: '#94a3b8', letterSpacing: '0', lineHeight: '28px', width: '100%', textAlign: 'center' }}>
                    {plan.features.map((feature, i) => (
                      <React.Fragment key={i}>
                        {feature}
                        {i < plan.features.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </Typography>
                </Box>
                <GetStartedButton featured={plan.featured} onClick={() => {
                  navigate('/login');
                }}>
                  Get Started
                </GetStartedButton>
              </Box>
            </PricingCard>
          </Box>
        ))}
      </Box>
      
    </SectionContainer>
  );
};

export default PricingPlansSection;
